import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Order {
  id: string;
  order_title: string;
  price?: number;
  paid?: boolean;
  payment_notified?: boolean;
}

interface BankSettings {
  bank_name: string;
  bank_logo_url: string;
  iban: string;
  account_holder: string;
}

export default function BankTransferPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [bankSettings, setBankSettings] = useState<BankSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const navigate = useNavigate();
  const customerEmail = localStorage.getItem('customerEmail');

  useEffect(() => {
    if (!customerEmail) {
      navigate('/customer-login');
      return;
    }
    fetchData();
  }, [customerEmail, navigate]);

  const fetchData = async () => {
    try {
      // Banka ayarlarını al
      const { data: bankData, error: bankError } = await supabase
        .from('bank_settings')
        .select('*')
        .maybeSingle();

      if (bankError) throw bankError;
      if (bankData) {
        setBankSettings(bankData);
      }

      // TÜM ödenmemiş siparişleri al (sepet kontrolü YOK)
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('id, order_title, price, paid, payment_notified')
        .eq('customer_email', customerEmail)
        .eq('paid', false)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;
      if (ordersData) {
        setOrders(ordersData);
        if (ordersData.length > 0 && !selectedOrderId) {
          setSelectedOrderId(ordersData[0].id);
        }
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyPayment = async () => {
    if (!selectedOrderId || !customerEmail) return;

    const selectedOrder = orders.find(o => o.id === selectedOrderId);
    
    // Fiyat belirlenmemişse uyarı göster
    if (!selectedOrder?.price || selectedOrder.price <= 0) {
      alert('⚠️ Bu sipariş için henüz fiyat belirlenmemiş. Lütfen yönetici ile iletişime geçin.');
      return;
    }

    setNotifying(true);
    try {
      // Edge function üzerinden güncelle (RLS bypass)
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'notifyPayment',
          orderId: selectedOrderId,
          customerEmail: customerEmail,
        }),
      });

      const result = await response.json();
      if (result.error) throw new Error(result.error);

      alert('✅ Ödeme bildirimi başarıyla gönderildi! Yönetici onayından sonra siparişiniz işleme alınacaktır.');
      navigate('/customer-dashboard');
    } catch (error) {
      console.error('Ödeme bildirimi hatası:', error);
      alert('❌ Ödeme bildirimi gönderilirken bir hata oluştu');
    } finally {
      setNotifying(false);
    }
  };

  const selectedOrder = orders.find(o => o.id === selectedOrderId);
  const totalAmount = selectedOrder?.price || 0;
  const hasPriceSet = selectedOrder?.price && selectedOrder.price > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-green-500 animate-spin"></i>
      </div>
    );
  }

  if (!bankSettings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center max-w-md">
          <i className="ri-error-warning-line text-5xl text-amber-500 mb-4"></i>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Banka Bilgileri Bulunamadı</h2>
          <p className="text-gray-600 mb-6">Lütfen yönetici ile iletişime geçin.</p>
          <button
            onClick={() => navigate('/customer-dashboard')}
            className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Siparişlerime Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/customer-dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm font-medium">Siparişlerime Dön</span>
          </button>
          <div className="flex items-center gap-2">
            <img 
              src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png" 
              alt="Logo" 
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Havale / EFT Sonrası Ödeme Bildirimi</h1>
        <p className="text-gray-600 mb-8">
          Havale/EFT işleminizi tamamladıktan sonra ödemenizi bu alandan bildirebilirsiniz.
        </p>

        {orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <i className="ri-file-list-line text-6xl text-gray-300 mb-4"></i>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Ödeme Bekleyen Sipariş Yok</h2>
            <p className="text-gray-500 mb-6">Tüm siparişleriniz ödenmiş durumda.</p>
            <button
              onClick={() => navigate('/customer-dashboard')}
              className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Siparişlerime Git
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sol Taraf - Banka Bilgileri */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              {/* Banka Logosu */}
              {bankSettings.bank_logo_url && (
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center border border-gray-200">
                  <img 
                    src={bankSettings.bank_logo_url} 
                    alt={bankSettings.bank_name}
                    className="h-16 w-auto object-contain"
                  />
                </div>
              )}

              {/* Bilgi Notu */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5 flex-shrink-0"></i>
                  <p className="text-blue-800 text-sm">
                    Öncelikle aşağıdaki banka bilgileri ile ödemeyi yapın
                  </p>
                </div>
              </div>

              {/* Ödeme Tutarı */}
              <div>
                <label className="text-gray-500 text-sm font-medium mb-2 block">Ödemeniz Gereken Tutar</label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {hasPriceSet ? (
                    <p className="text-3xl font-bold text-gray-900">{totalAmount.toFixed(2)} TL</p>
                  ) : (
                    <div className="text-center">
                      <p className="text-amber-600 font-semibold mb-1">Fiyat Belirlenmedi</p>
                      <p className="text-gray-500 text-xs">Yönetici fiyat belirledikten sonra ödeme yapabilirsiniz</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Banka Adı */}
              <div>
                <label className="text-gray-500 text-sm font-medium mb-2 block uppercase tracking-wider">BANKA</label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 font-semibold text-lg">{bankSettings.bank_name}</p>
                </div>
              </div>

              {/* IBAN */}
              <div>
                <label className="text-gray-500 text-sm font-medium mb-2 block uppercase tracking-wider">IBAN</label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-center justify-between gap-3">
                  <p className="text-gray-900 font-mono text-base">{bankSettings.iban}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(bankSettings.iban);
                      alert('IBAN kopyalandı!');
                    }}
                    className="w-9 h-9 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                    title="IBAN'ı Kopyala"
                  >
                    <i className="ri-file-copy-line text-gray-600 text-lg"></i>
                  </button>
                </div>
              </div>

              {/* Alıcı */}
              <div>
                <label className="text-gray-500 text-sm font-medium mb-2 block uppercase tracking-wider">ALICI</label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-center justify-between gap-3">
                  <p className="text-gray-900 font-semibold">{bankSettings.account_holder}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(bankSettings.account_holder);
                      alert('Alıcı adı kopyalandı!');
                    }}
                    className="w-9 h-9 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                    title="Alıcı Adını Kopyala"
                  >
                    <i className="ri-file-copy-line text-gray-600 text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Sağ Taraf - Ödeme Bildirimi */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Havale / EFT Sonrası Ödeme Bildirimi</h2>
              
              <div className="space-y-6">
                {/* Sipariş Seçimi */}
                <div>
                  <label className="text-gray-700 text-sm font-medium mb-2 block">
                    Hangi sipariş için ödeme yaptınız?
                  </label>
                  <select
                    value={selectedOrderId}
                    onChange={(e) => setSelectedOrderId(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none cursor-pointer"
                  >
                    {orders.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.order_title}
                        {order.price && order.price > 0 ? ` - ${order.price.toFixed(2)} TL` : ' - Fiyat Belirlenmedi'}
                        {order.payment_notified ? ' (Bildirim Yapıldı)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Seçili Sipariş Bilgisi */}
                {selectedOrder && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-text-line text-2xl text-green-600"></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-gray-900 font-semibold mb-1">{selectedOrder.order_title}</h3>
                        <p className="text-gray-500 text-sm mb-2">
                          Sipariş No: #{selectedOrder.id.slice(0, 8).toUpperCase()}
                        </p>
                        {hasPriceSet ? (
                          <p className="text-green-600 font-bold text-xl">
                            {selectedOrder.price?.toFixed(2)} TL
                          </p>
                        ) : (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
                            <p className="text-amber-700 text-xs flex items-center gap-1">
                              <i className="ri-alert-line"></i>
                              Fiyat henüz belirlenmedi
                            </p>
                          </div>
                        )}
                        {selectedOrder.payment_notified && (
                          <div className="mt-3 flex items-center gap-2 text-amber-600 text-sm">
                            <i className="ri-time-line"></i>
                            <span>Bu sipariş için ödeme bildirimi yapılmış, yönetici onayı bekleniyor</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Uyarı */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-error-warning-line text-amber-600 text-xl mt-0.5 flex-shrink-0"></i>
                    <div className="text-amber-800 text-sm">
                      <p className="font-medium mb-2">Önemli Notlar:</p>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>Ödeme bildirimi yapmadan önce havale/EFT işleminizi tamamladığınızdan emin olun</li>
                        <li>Yönetici onayından sonra siparişiniz işleme alınacaktır</li>
                        {!hasPriceSet && (
                          <li className="text-amber-900 font-medium">Fiyat belirlenmeden ödeme bildirimi yapamazsınız</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Bildirim Butonu */}
                <button
                  onClick={handleNotifyPayment}
                  disabled={notifying || !selectedOrderId || selectedOrder?.payment_notified || !hasPriceSet}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {notifying ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      Bildiriliyor...
                    </>
                  ) : selectedOrder?.payment_notified ? (
                    <>
                      <i className="ri-checkbox-circle-line text-xl"></i>
                      Ödeme Bildirildi
                    </>
                  ) : !hasPriceSet ? (
                    <>
                      <i className="ri-lock-line text-xl"></i>
                      Fiyat Belirlenmedi
                    </>
                  ) : (
                    <>
                      <i className="ri-notification-line text-xl"></i>
                      Ödemeyi Bildir
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
                  <i className="ri-shield-check-line text-green-500"></i>
                  <span>Güvenli işlem</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
